import { Controller } from "@hotwired/stimulus";
import { isRangingStatusQuestion, renderRangingConfirm } from "../../lib/ranging_confirm_renderer";

export default class SurveyBaseController extends Controller {

  connect() {
    this.registerCustomQuestions();
  }

  initializeSurvey(mode) {
    const survey = new Survey.Model();
    survey.mode = mode;
    // Cap year to 4 digits on all date inputs — SurveyJS renders async so the
    // global capDateInputs in application.js doesn't reach these inputs.
    survey.onAfterRenderQuestion.add((_, options) => {
      options.htmlElement.querySelectorAll('input[type="date"]').forEach(input => {
        if (!input.max) input.max = "9999-12-31";
      });
    });
    this._registerMatrixDropdownSearch(survey);
    this._registerRangingConfirm(survey);
    return survey;
  }

  // Ports rt-client's matrixdropdown row search (card #3070). Injects a
  // search input above any matrixdropdown question that has rows, so
  // Taskers can filter long row lists (most visibly ranging-status-*
  // questions with dozens of SKU rows). Shared across every survey render
  // pathway in the app (offline app, result editor, dashboard, tabulator)
  // because they all go through initializeSurvey.
  //
  // Mechanism: each row ItemValue gets a visibleIf expression matching a
  // per-row lowercase text/value blob against a search survey variable.
  // Typing updates the variable and re-runs the question's condition --
  // rows are hidden/shown via SurveyJS's own visibleIf, not direct DOM
  // hiding, so validation/completion logic keeps working as normal.
  _registerMatrixDropdownSearch(survey) {
    survey.onAfterRenderQuestion.add((_sender, options) => {
      const question = options.question;
      if (question.getType() !== "matrixdropdown" || !question.rows?.length) return;

      const htmlElement = options.htmlElement;

      if (htmlElement.querySelector(".matrix-dropdown-search-wrapper")) return; // search UI already wired for this render

      const searchVarName = `search_${question.name}`;
      const searchLowerVarName = `${searchVarName}_lower`;

      // Wire row-level visibleIf once per question instance. Re-running this
      // on every re-render (e.g. paging back to this question) would reset
      // the search variable to "" via setVariable, wiping whatever the
      // Tasker had already typed.
      if (!question.__matrixSearchWired) {
        question.__matrixSearchWired = true;
        // A search term matching no rows leaves every row hidden by visibleIf,
        // and SurveyJS then hides the whole question (and with it the search
        // box, leaving "The survey doesn't contain any visible elements").
        // Keep the question on screen with an empty row list instead.
        question.hideIfRowsEmpty = false;
        survey.setVariable(searchVarName, "");
        survey.setVariable(searchLowerVarName, "");

        question.rows.forEach(rowItem => {
          const textLower = String(rowItem.text || "").toLowerCase();
          const valueLower = String(rowItem.value || "").toLowerCase();
          const rowSearchVar = `${searchVarName}_row_${rowItem.value}`;
          survey.setVariable(rowSearchVar, `${textLower} ${valueLower}`);
          rowItem.visibleIf = `{${searchVarName}} empty or {${rowSearchVar}} contains {${searchLowerVarName}}`;
        });
      }

      this._injectMatrixSearchInput(survey, question, htmlElement, searchVarName, searchLowerVarName);
    });
  }

  // Cards #4031/#3073 -- compact tap-row renderer for ranging-status-* questions
  // (see lib/ranging_confirm_renderer.js). Active only when the page has loaded a
  // last-visit hints payload (this._rangingHints), which the server sends only
  // when field:ranging_confirm is on for the user; otherwise the native
  // SurveyJS matrix renders exactly as before. Registered after the search
  // hook so the search bar already exists when the rows are drawn.
  _registerRangingConfirm(survey) {
    survey.onAfterRenderQuestion.add((_sender, options) => {
      if (!this._rangingHints || !isRangingStatusQuestion(options.question)) return

      renderRangingConfirm({
        survey,
        question: options.question,
        htmlElement: options.htmlElement,
        hints: this._rangingHints,
        barcodes: this._rangingBarcodes || {},
        // Card #4692 -- open allocation/TIO incoming-stock badge.
        incoming: this._rangingIncoming || {},
        searchVarName: `search_${options.question.name}`
      })
    })
  }

  _injectMatrixSearchInput(survey, question, htmlElement, searchVarName, searchLowerVarName) {
    const wrapper = document.createElement("div");
    wrapper.className = "matrix-dropdown-search-wrapper relative w-full mb-3";

    const input = document.createElement("input");
    input.type = "text";
    input.className = "sd-input sd-text w-full pr-8";
    input.placeholder = "Search rows…";

    const clearButton = document.createElement("button");
    clearButton.type = "button";
    clearButton.innerHTML = "&times;";
    clearButton.setAttribute("aria-label", "Clear search");
    clearButton.className = "hidden absolute right-2 top-1/2 -translate-y-1/2 text-lg leading-none text-slate-400 hover:text-slate-600 px-1";

    wrapper.appendChild(input);
    wrapper.appendChild(clearButton);

    const contentContainer = htmlElement.querySelector(".sd-element__content");
    if (contentContainer?.firstChild) {
      contentContainer.insertBefore(wrapper, contentContainer.firstChild);
    } else {
      htmlElement.insertBefore(wrapper, htmlElement.firstChild);
    }

    input.addEventListener("input", () => {
      const term = input.value.trim();
      const termLower = term.toLowerCase();

      // BUGFIX ported from rt-client: setVariable triggers visibleIf
      // re-evaluation on every row, which resets question.value for rows
      // that toggle visibility. Snapshot before, restore after if changed --
      // without this, typing in the search box silently wipes answers on
      // rows that get hidden/reshown.
      const savedValue = JSON.parse(JSON.stringify(question.value || {}));

      survey.setVariable(searchVarName, term);
      survey.setVariable(searchLowerVarName, termLower);

      if (JSON.stringify(question.value) !== JSON.stringify(savedValue)) {
        question.value = savedValue;
      }

      question.runCondition(survey.getFilteredValues(), survey.getFilteredProperties());
      clearButton.classList.toggle("hidden", !input.value);
      htmlElement.dispatchEvent(new CustomEvent("matrix-search-changed"));
    });

    clearButton.addEventListener("click", () => {
      input.value = "";
      input.dispatchEvent(new Event("input"));
      input.focus();
    });
  }

  registerCustomQuestions() {
    this.registerCameraUploadQuestion();
    this.registerMultiCameraUploadQuestion();
    this.registerCameraModeProperty();
    this.registerCountrySelectDropdown();
    this.registerSurveyVersionProperty();
    this.registerTaskIdProperty();
    this.registerSourceObjectiveIdProperty();
    this.registerTaskTagNamesProperty();
    this.registerTaskDefaultChartTypeProperty();
    this.registerTaskHideChartProperty();
  }

  registerCameraUploadQuestion() {
    if (Survey.ComponentCollection.Instance.getCustomQuestionByName("camera-upload-question")) return;
    Survey.ComponentCollection.Instance.add({
      // A unique name; must use lowercase
      name: "camera-upload-question",
      // A display name used in the Toolbox
      title: "Take a Photo",
      // A default title for questions created with this question type
      defaultQuestionTitle: "Take a photo",
      iconName: "icon-camera-24x24",
      // A JSON schema for the base question type (Dropdown in this case)
      questionJSON: {
        "type": "file",
        "sourceType": "camera",
        "category": "general",
        "visibleIndex": 0
      },
      // Inherit all or individual properties from the base question type
      inheritBaseProps: true // or [ "allowClear" ]
    });
  }

  registerMultiCameraUploadQuestion() {
    if (Survey.ComponentCollection.Instance.getCustomQuestionByName("camera-mult-upload-question")) return;
    Survey.ComponentCollection.Instance.add({
      // A unique name; must use lowercase
      name: "camera-mult-upload-question",
      // A display name used in the Toolbox
      title: "Take Multiple Photos",
      // A default title for questions created with this question type
      defaultQuestionTitle: "Take 3 photos",
      iconName: "icon-camera-24x24",
      // A JSON schema for the base question type (Dropdown in this case)
      questionJSON: {
        "type": "file",
        "sourceType": "camera",
        "category": "general",
        "visibleIndex": 0
      },
      // Inherit all or individual properties from the base question type
      inheritBaseProps: true // or [ "allowClear" ]
    });
  }


  registerCameraModeProperty() {
    // Records the ORIGINAL camera question type after the field result editor
    // rewrites it to a plain "file" question (convertPhotoTypeQuestionsToFileUpload).
    // The field photo camera reads this to decide single vs multi capture. Not
    // visible in the survey creator; data-only provenance.
    if (Survey.Serializer.findProperty("question", "cameraMode")) return;
    Survey.Serializer.addProperty("question", {
      name: "cameraMode",
      type: "string",
      category: "data",
      isSerializable: true,
      visible: false
    });
  }

  registerCountrySelectDropdown() {
    if (Survey.ComponentCollection.Instance.getCustomQuestionByName("country")) return;
    Survey.ComponentCollection.Instance.add({
      // A unique name; must use lowercase
      name: "country",
      // A display name used in the Toolbox
      title: "Country",
      // A default title for questions created with this question type
      defaultQuestionTitle: "Country",
      //
      iconName: "icon-toolbox-dropdown-24x24",
      // A JSON schema for the base question type (Dropdown in this case)
      questionJSON: {
        "type": "dropdown",
        "placeholder": "Select a country...",
        "choicesByUrl": {
          "url": "https://surveyjs.io/api/CountriesExample",
          "category": "general",
          "visibleIndex": 10
        }
      },
      // Inherit all or individual properties from the base question type
      inheritBaseProps: true // or [ "allowClear" ]
    });
  }

  registerSurveyVersionProperty() {
    Survey.Serializer.addProperty("survey", {
      name: "surveyVersion",
      type: "number",
      category: "general",
      visibleIndex: 0
    });
  }

  registerTaskIdProperty() {
    // Add a property to the base Question class and to all questions as a result
    Survey.Serializer.addProperty("panel", {
      name: "taskId",
      type: "number",
      category: "data",
      default: 1,
      visibleIndex: 0,
      readOnly: true,
      visible: true
    });
    // Add a property to the base Question class and to all questions as a result
    Survey.Serializer.addProperty("question", {
      name: "taskId",
      type: "number",
      category: "data",
      default: 1,
      visibleIndex: 0,
      readOnly: true,
      visible: true
    });
  }

  registerSourceObjectiveIdProperty() {
    // Provenance: tracks which Objective a Task was derived from. Read-only.
    Survey.Serializer.addProperty("panel", {
      name: "sourceObjectiveId",
      type: "number",
      category: "data",
      visibleIndex: 1,
      readOnly: true,
      visible: true
    });
    Survey.Serializer.addProperty("question", {
      name: "sourceObjectiveId",
      type: "number",
      category: "data",
      visibleIndex: 1,
      readOnly: true,
      visible: true
    });
  }

  async registerTaskTagNamesProperty() {
    try {
      const choices = await this.fetchTags('photos');
      // Allows survey creators to tag questions with tags
      Survey.Serializer.addProperty("question", {
        name: "tagNames",
        displayName: "Tags",
        type: "multiplevalues",
        placeholder: "Select Tag Names...",
        // display the tag names in the survey JSON schema
        isSerializable: true,
        choices: choices,
        category: "general",
        visibleIndex: 0,
        readOnly: false,
        visible: true
      });
    } catch (error) {
      console.error('Error fetching tags for tagNames property:', error);
    }
  }

  registerTaskDefaultChartTypeProperty() {
    // feeds into the defaultChartType when creating dashboard visuals
    Survey.Serializer.addProperty("question", {
      name: "defaultChartType",
      type: "dropdown",
      choices: ["", "bar", "vbar", "pie", "doughnut", "stackedbar", "gauge", "bullet", "table"],
      category: "data",
      isRequired: false,
      visibleIndex: 4,
      readOnly: false,
      visible: true
    });
  }

  registerTaskHideChartProperty() {
    // feeds into the defaultChartType when creating dashboard visuals
    Survey.Serializer.addProperty("question", {
      name: "hideChart",
      type: "boolean",
      category: "data",
      isRequired: false,
      visibleIndex: 3,
      readOnly: false,
      visible: true
    });
  }

  async fetchTags(kind) {
    const url = `/api/v1/tags.json?kind=${kind}`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        // if you see values in the dropdown that are not
        // in the json data (or replacing it) then you may
        // be using a reserved SurveyJS name
        return data.map(tag => tag.name);
      } else {
        return [];
      }
    } catch (error) {
      console.error('There has been a problem fetching tags.', error);
      return []; // Return an empty array in case of an error
    }
  }
}